/**
 * ADR-0068: Scope-aware PDOs list at /app/workspace/pdos.
 * Editor stays at /app/build/pdos/:id.
 * ADR-0092: Source column + filter + "+ Upload PDO" entry point for the new
 * upload-from-file flow. PDOs from operation graphs are still created
 * automatically by persist nodes in operations (no button here).
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { ScopePill } from "../components/ScopePill.js";
import { ScopedRowActionsMenu } from "../components/ScopedRowActionsMenu.js";
import { ScopeTabs, useScopeTabFromUrl, useResolvedScopeTab, useScopeTabsBasePath } from "../components/ScopeTabs.js";
import { apiFetch } from "../lib/api.js";
import { useMyGroups, type ScopeCounts, type ScopedEntityFields, useMyAccountId } from "../lib/scope.js";
import type { PdoSourceType, UploadParser } from "../lib/pdoUploadTypes.js";

type ScopedPdoListItem = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  status: "active" | "draft" | "archived";
  updated_at: string;
  // ADR-0092 — source-type column for the unified catalog
  source_type: PdoSourceType;
  upload_parser: UploadParser | null;
} & ScopedEntityFields;

type SourceFilter = "all" | "upstream" | "upload";

function sourceLabel(item: { source_type: PdoSourceType; upload_parser: UploadParser | null }): string {
  if (item.source_type === "upstream") return "Graph";
  if (item.upload_parser) return `Upload (.${item.upload_parser})`;
  return "Upload";
}

export function WorkspacePDOs({ callerAccountId, tenantId, entitlement }: { callerAccountId: string; tenantId: string; entitlement: "viewer" | "builder" | "admin" }) {
  const tab = useScopeTabFromUrl();
  const basePath = useScopeTabsBasePath();
  const activeTab = useResolvedScopeTab({ tenantId, accountId: callerAccountId, entity: "pdos" });
  const { groups } = useMyGroups();
  const myAccountId = useMyAccountId();
  const inZeroGroups = groups.length === 0 && entitlement !== "admin";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ScopedPdoListItem[]>([]);
  const [counts, setCounts] = useState<ScopeCounts | null>(null);
  const [q, setQ] = useState("");
  // ADR-0092 — client-side Source filter (Graph / Upload / All). The list
  // endpoint returns all rows; filtering happens here. If the catalog grows
  // significantly we can push this down to the server, but for v1 the row
  // count is small enough that client-side is fine.
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");

  const extraSearch = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    return p;
  }, [q]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("tab", activeTab);
      if (q.trim()) params.set("q", q.trim());
      const [listRes, countsRes] = await Promise.all([
        apiFetch<{ pdos: ScopedPdoListItem[] }>(`/api/tenant/pdos?${params.toString()}`),
        apiFetch<{ counts: ScopeCounts }>(`/api/tenant/pdos?counts_only=1`)
      ]);
      setRows(listRes.pdos);
      setCounts(countsRes.counts);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeTab]);
  useEffect(() => { const t = setTimeout(() => { void load(); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [q]);

  return (
    <PageLayout title="Persistent Data Objects">
      <div style={{ display: "grid", gap: 12 }}>
        <ScopeTabs
          currentTab={tab}
          counts={counts}
          basePath={basePath}
          extraSearch={extraSearch}
          inZeroGroups={inZeroGroups}
          storageFingerprint={{ tenantId, accountId: callerAccountId, entity: "pdos" }}
        />
        <div className="toolbar">
          <div className="toolbarGroup">
            <input
              className="input"
              style={{ width: 260 }}
              placeholder="Search PDOs…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <span className="muted" style={{ fontSize: 12 }}>Source</span>
            <select
              className="select"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
            >
              <option value="all">All</option>
              <option value="upstream">Graph</option>
              <option value="upload">Upload</option>
            </select>
          </div>
          {(entitlement === "builder" || entitlement === "admin") && (
            <div className="toolbarGroup">
              <Link className="btn btnPrimary" to={`/app/workspace/pdos/new-from-upload?tab=${encodeURIComponent(activeTab)}`}>
                + Upload PDO
              </Link>
            </div>
          )}
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          PDOs from operation graphs are created automatically by persist nodes — see Operations.
        </div>
        {error ? <ErrorState detail={error} /> : null}
        {loading ? (
          <LoadingState />
        ) : (() => {
          const filteredRows = sourceFilter === "all"
            ? rows
            : rows.filter((r) => r.source_type === sourceFilter);
          if (filteredRows.length === 0) return <EmptyPdoState tab={activeTab} inZeroGroups={inZeroGroups} />;
          return (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Source</th>
                    <th>Scope</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th style={{ width: 40 }} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.id}>
                      <td><Link to={`/app/build/pdos/${encodeURIComponent(r.id)}`}>{r.name}</Link></td>
                      <td className="mono" style={{ fontSize: 12 }}>{r.slug}</td>
                      <td>
                        <span className={`pill ${r.source_type === "upload" ? "pillWarn" : "pillNeutral"}`}>
                          {sourceLabel(r)}
                        </span>
                      </td>
                      <td><ScopePill scope={r.scope} groupName={r.owner_group_name} ownerDisplayName={r.owner_display_name} ownerIsViewer={r.owner_account_id === myAccountId} /></td>
                      <td><span className="pill">{r.status}</span></td>
                      <td className="muted">{new Date(r.updated_at).toLocaleDateString()}</td>
                      <td style={{ textAlign: "right" }}>
                        <ScopedRowActionsMenu entityKind="pdo" entityId={r.id} entityName={r.name} onDeleted={load} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
    </PageLayout>
  );
}

function EmptyPdoState(props: { tab: "mine" | "teams" | "shared"; inZeroGroups: boolean }) {
  if (props.tab === "mine") return <div className="muted" style={{ padding: "24px 12px" }}>No personal PDOs. PDOs are produced by operations you own.</div>;
  if (props.tab === "teams") {
    if (props.inZeroGroups) return <div className="muted" style={{ padding: "24px 12px" }}>You are not a member of any team yet.</div>;
    return <div className="muted" style={{ padding: "24px 12px" }}>No team PDOs in your teams.</div>;
  }
  return <div className="muted" style={{ padding: "24px 12px" }}>No shared PDOs in this tenant yet.</div>;
}
